/**
 * @file CameraScanDialog.tsx
 * @description Диалог «Скан камерой» у поля склада (phase19.22 шаг 5):
 *   задняя камера телефона, рамка-видоискатель, подсказка, фонарик и
 *   «Отмена». На `<sm` — на весь экран (правило mobile-first).
 *
 * Первый распознанный код закрывает диалог и уходит в `onDetected` — дальше
 * поле скана ведёт себя так же, как после USB-сканера. Камера и декодер —
 * в `useCameraScanner`; здесь только раскладка.
 */

import { Flashlight, FlashlightOff, Loader2, VideoOff } from "lucide-react";

import { Button } from "./button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./dialog";
import { useCameraScanner } from "./useCameraScanner";

interface Props {
  open: boolean;
  /** Закрытие; `noCamera` — камеры на устройстве нет, кнопку пора прятать. */
  onClose: (info: { noCamera: boolean }) => void;
  /** Код распознан: стрим уже остановлен, диалог закроет вызывающий. */
  onDetected: (code: string) => void;
}

export function CameraScanDialog({ open, onClose, onDetected }: Props) {
  const camera = useCameraScanner({ open, onDetected });
  const close = () => onClose({ noCamera: camera.noCamera });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent fullScreen showClose={false} className="gap-3 p-0 sm:p-4">
        <DialogTitle className="sr-only">Скан камерой</DialogTitle>

        {/* Кадр камеры: на телефоне занимает всё, что осталось над кнопками. */}
        <div className="relative min-h-[50vh] flex-1 overflow-hidden bg-on-surface sm:aspect-[4/3] sm:min-h-0 sm:flex-none sm:rounded-lg">
          <video
            ref={camera.setVideo}
            playsInline
            muted
            autoPlay
            className="absolute inset-0 size-full object-cover"
          />
          {camera.status !== "error" ? (
            <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
              {/* Затемнение вокруг рамки — тенью, чтобы рамка оставалась «окном». */}
              <div className="aspect-square w-3/5 max-w-[280px] rounded-2xl border-2 border-surface-lowest [box-shadow:0_0_0_9999px_rgba(0,0,0,0.45)]" />
            </div>
          ) : null}
          {camera.status === "starting" ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="size-8 animate-spin text-surface-lowest" aria-label="Включаем камеру" />
            </div>
          ) : null}
          {camera.status === "error" ? (
            <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-surface-lowest">
              <VideoOff className="size-8" aria-hidden />
              <p className="text-base">{camera.error}</p>
            </div>
          ) : null}
        </div>

        <DialogDescription className="px-4 text-center sm:px-0">
          Наведите на DataMatrix или штрих-код
        </DialogDescription>

        <div className="flex gap-2 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-0 sm:pb-0">
          {camera.torchAvailable ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-14 flex-1"
              aria-pressed={camera.torchOn}
              onClick={() => void camera.toggleTorch()}
            >
              {camera.torchOn ? <FlashlightOff className="size-5" /> : <Flashlight className="size-5" />}
              <span className="ml-2">{camera.torchOn ? "Выключить свет" : "Фонарик"}</span>
            </Button>
          ) : null}
          <Button type="button" variant="outline" className="min-h-14 flex-1" onClick={close}>
            Отмена
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
